import { connect, ConnectedProps } from 'react-redux';
import { useParams } from 'react-router-dom-v5-compat';

import { MessageType } from 'dashboard/lib/interfaces/Message';
import { productFeatureDisabled } from 'dashboard/lib/productFeature';
import { ProductFeatureName } from 'dashboard/models/organization';
import { showMessage } from 'dashboard/redux/actions/messages';
import { RootState } from 'dashboard/redux/store';
import {
  activateTransactionStatus,
  addTransactionStatus,
  deactivateTransactionStatus,
  loadTransactionStatusesByTransactionType,
  renameTransactionStatus,
} from 'dashboard/redux/thunk-actions/transaction-statuses';
import {
  loadTransactionType,
  setDefaultTransactionStatus,
} from 'dashboard/redux/thunk-actions/transaction-type';
import DashboardTransactionStatusesView from 'dashboard/views/DashboardTransactionStatusesView';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiErrorHandler from 'lib/hooks/useApiErrorHandler';
import useApiLoader from 'lib/hooks/useApiLoader';
import { isPermissionsError, permissionsErrorMessage } from 'lib/permissions-errors';

type Params = { transactionTypeId: string };

const DashboardTransactionStatusesRoute = (props: PropsFromRedux) => {
  const { currentEnvironment, organization } = props;
  const params = useParams<Params>() as Params;
  const productFeatureIsDisabled = productFeatureDisabled(
    organization,
    ProductFeatureName.TransactionsDashboard,
    currentEnvironment?.type,
  );

  const { callback: handleLoadTransactionStatusesByTransactionType } = useApiLoader({
    loader: () => props.loadTransactionStatusesByTransactionType(params.transactionTypeId),
    deps: [props.loadTransactionStatusesByTransactionType, params.transactionTypeId],
    skip: productFeatureIsDisabled,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load transaction statuses.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const { callback: handleLoadTransactionType } = useApiLoader({
    loader: () => props.loadTransactionType(params.transactionTypeId),
    deps: [props.loadTransactionType, params.transactionTypeId],
    skip: productFeatureIsDisabled,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load transaction type.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const { error: handleAddTransactionStatusError, callback: handleAddTransactionStatus } =
    useApiCallback(
      async (name: string, transactionTypeId: string) => {
        await props.addTransactionStatus(name, transactionTypeId);
        props.showMessage('Successfully added new transaction status.', MessageType.Success);
        handleLoadTransactionStatusesByTransactionType();
      },
      [handleLoadTransactionStatusesByTransactionType],
    );

  useApiErrorHandler({
    error: handleAddTransactionStatusError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage(
          'Failed to add new transaction status, please try again.',
          MessageType.Failure,
          false,
        );
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const {
    error: handleDeactivateTransactionStatusError,
    callback: handleDeactivateTransactionStatus,
  } = useApiCallback(
    async (name: string) => {
      await props.deactivateTransactionStatus(name);
      props.showMessage('Successfully deactivated transaction status.', MessageType.Success);
      handleLoadTransactionStatusesByTransactionType();
    },
    [handleLoadTransactionStatusesByTransactionType],
  );

  useApiErrorHandler({
    error: handleDeactivateTransactionStatusError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage(
          'Transactions exist with this status. Update those transactions to a different status and try again.',
          MessageType.Failure,
          false,
        );
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const { error: handleActivateTransactionStatusError, callback: handleActivateTransactionStatus } =
    useApiCallback(
      async (transactionStatusId: string) => {
        await props.activateTransactionStatus(transactionStatusId);
        props.showMessage('Successfully activated transaction status.', MessageType.Success);
        handleLoadTransactionStatusesByTransactionType();
      },
      [handleLoadTransactionStatusesByTransactionType],
    );

  useApiErrorHandler({
    error: handleActivateTransactionStatusError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage(
          'Failed to activate transaction status, please try again.',
          MessageType.Failure,
          false,
        );
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const { error: handleRenameTransactionStatusError, callback: handleRenameTransactionStatus } =
    useApiCallback(
      async (name: string, transactionStatusId: string) => {
        await props.renameTransactionStatus(name, transactionStatusId);
        props.showMessage('Successfully renamed transaction status.', MessageType.Success);
        handleLoadTransactionStatusesByTransactionType();
      },
      [handleLoadTransactionStatusesByTransactionType],
    );

  useApiErrorHandler({
    error: handleRenameTransactionStatusError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage(
          'Failed to rename new Transaction status, please try again.',
          MessageType.Failure,
          false,
        );
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const {
    error: handleSetDefaultTransactionStatusError,
    callback: handleSetDefaultTransactionStatus,
  } = useApiCallback(
    async (transactionStatusId: string) => {
      if (!props.transactionType) {
        props.showMessage('Failed to set default transaction status.', MessageType.Failure);
        return;
      }
      props.setDefaultTransactionStatus(props.transactionType.id, transactionStatusId);
      handleLoadTransactionType();
    },
    [props.transactionType, handleLoadTransactionType],
  );

  useApiErrorHandler({
    error: handleSetDefaultTransactionStatusError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage(
          'Failed to set default transaction status, please try again.',
          MessageType.Failure,
          false,
        );
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  return (
    <>
      {props.transactionStatuses && props.transactionType?.attributes && (
        <DashboardTransactionStatusesView
          transactionStatuses={props.transactionStatuses}
          transactionType={props.transactionType}
          handleAddTransactionStatus={handleAddTransactionStatus}
          handleDeactivateTransactionStatus={handleDeactivateTransactionStatus}
          handleRenameTransactionStatus={handleRenameTransactionStatus}
          handleActivateTransactionStatus={handleActivateTransactionStatus}
          handleSetDefaultTransactionStatus={handleSetDefaultTransactionStatus}
          productFeatureIsDisabled={productFeatureIsDisabled}
        />
      )}
    </>
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    transactionStatuses: state.transactionStatuses.transactionStatuses,
    transactionType: state.transactionType.transactionType,
    currentEnvironment: state.currentEnvironment.currentEnvironment,
    organization: state.organization.organization,
  };
};

const mapDispatchToProps = {
  addTransactionStatus,
  deactivateTransactionStatus,
  loadTransactionStatusesByTransactionType,
  loadTransactionType,
  renameTransactionStatus,
  activateTransactionStatus,
  setDefaultTransactionStatus,
  showMessage,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DashboardTransactionStatusesRoute);
